import { useRouterState } from '@tanstack/react-router'

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /**
     * A visitor on this route is not expected to HAVE a session — an auth
     * screen, or a page whose credential is a token in the URL.
     *
     * Chrome mounted at the ROOT (which renders on every route, including these)
     * reads it to skip an authenticated query that would only ever 401 here.
     *
     * **Declared on the route, and absence means "query as normal".** That
     * failure direction is the whole design: forget the flag on a new public
     * route and you get the old behaviour — one wasted request. Put it on a
     * route where a session IS possible and identity-dependent chrome silently
     * disappears there, which is the expensive mistake.
     *
     * **The test for it is "would a signed-in person be HERE", not "does this
     * page act on their account".** Those read as the same question and are not.
     * `/`, `/help`, `/terms`, `/privacy`, `/disclaimer` and `/offline` act on
     * no account, and all six are ordinary places for a signed-in person to be:
     * the checkout header's logo links to `/`, the consent banner links to
     * `/privacy` from every page, and `/help` is a PWA shortcut. An operator
     * mid-impersonation who follows one of those must still see the "Viewing
     * as X — Stop" pill, which is the affordance the banner exists for and,
     * since `/ops` refuses an impersonated session, their only in-app way out;
     * flagging those pages would take it away. The flag is the sign-in surface
     * plus `/read`.
     */
    anonymous?: boolean
  }
}

/**
 * Is the route being rendered one that expects no session?
 *
 * The alternative shapes were both worse. A pathname denylist inside the
 * component restates every public path in a second place and drifts on the
 * first rename; an allowlist of AUTHENTICATED layout ids fails the dangerous
 * way round — a route added outside those layouts silently loses the chrome.
 * A per-route flag puts the fact where the route is, and defaults to the safe
 * answer.
 */
export function useIsAnonymousRoute(): boolean {
  return useRouterState({
    select: (state) =>
      state.matches.some((match) => match.staticData.anonymous === true),
  })
}
